import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Alert,
  Button,
  Checkbox,
  Checklist,
  Drawer,
  Select,
  Field,
  Table
} from '@itds/elements/index.js';
import { STATES, PAYMENT_MODES } from '@itds/Constants';

import EditAgeRate from './EditAgeRate';

import Api from '@carrier/utils/Api';

const Edit = ({
  bands,
  healthCategories,
  modalFactors,
  onClose,
  onSave,
  products,
  quoteRate,
  riders,
  show,
  tableRates
}) => {
  const [data, setData] = useState({
    id: null,
    carrier_id: null,
    data_version_id: null,
    product_id: '',
    band_id: '',
    modal_factor_id: '',
    health_category_id: '',
    gender: 'Both',
    table_rate_id: '',
    col_type: 'Normal',
    states: [],
    adb_rider_ids: [],
    child_rider_ids: [],
    child_wop_rider_ids: [],
    wop_rider_ids: [],
    age_rates: []
  });
  const [age_rate, setAgeRate] = useState(null);
  const [save_error, setSaveError] = useState(false);

  useEffect(() => {
    const new_quote_rate = quoteRate || {};
    const states = [];

    STATES.forEach(state => {
      if (new_quote_rate[`state_${state.value}`]) {
        states.push(state.value);
      }
    });

    setData({
      id: new_quote_rate.id,
      carrier_id: new_quote_rate.carrier_id,
      data_version_id: new_quote_rate.data_version_id,
      product_id: new_quote_rate.product_id || products[0].id,
      band_id: new_quote_rate.band_id || bands[0].id,
      modal_factor_id: new_quote_rate.modal_factor_id || modalFactors[0].id,
      health_category_id: new_quote_rate.health_category_id || healthCategories[0].id,
      gender: new_quote_rate.gender || 'Both',
      table_rate_id: new_quote_rate.table_rate_id || '',
      col_type: new_quote_rate.col_type || 'Normal',
      states,
      adb_rider_ids: new_quote_rate.adb_rider_ids || [],
      child_rider_ids: new_quote_rate.child_rider_ids || [],
      child_wop_rider_ids: new_quote_rate.child_wop_rider_ids || [],
      wop_rider_ids: new_quote_rate.wop_rider_ids || [],
      age_rates: new_quote_rate.age_rates || []
    });
  }, [quoteRate]);

  const handleInputChange = (name, e) => {
    // eslint-disable-next-line eqeqeq
    const healthCategoryObj = healthCategories.filter(i => i.id == e.target.value);

    if (name === 'health_category_id') {
      if (healthCategoryObj[0].tobacco_category_type === 'Tobacco') {
        // @ts-ignore
        setData({ ...data, [name]: e.target.value, tobacco: true });
      } else {
        // @ts-ignore
        setData({ ...data, [name]: e.target.value, tobacco: false });
      }
    } else {
      setData({ ...data, [name]: e.target.value });
    }
  };

  const handleChecklistChange = (name, selected) => {
    setData({ ...data, [name]: selected });
  };

  const handleColumnTypeChange = e => {
    setData({ ...data, col_type: e.target.checked ? 'Table Rate' : 'Normal' });
  };

  const handleSave = () => {
    const { id, states, ...payload } = data;

    states.forEach(state => {
      payload[`state_${state}`] = true;
    });

    if (id) {
      Api.updateQuoteRate(id, payload)
        .then(() => {
          onSave();
          setSaveError(false);
        })
        .catch(() => {
          setSaveError(true);
          setTimeout(() => {
            setSaveError(false);
          }, 2000);
        });
    } else {
      Api.createQuoteRate(payload)
        .then(() => {
          onSave();
          setSaveError(false);
        })
        .catch(() => {
          setSaveError(true);
          setTimeout(() => {
            setSaveError(false);
          }, 2000);
        });
    }
  };

  const toggleAgeRateArchive = (age_rate, e) => {
    e.stopPropagation();

    Api.updateQuoteRate(data.id, {
      age_rates_attributes: [{ id: age_rate.id, is_archived: !age_rate.is_archived }]
    }).then(() => {
      onSave();
    });
  };

  return (
    <>
      <Drawer
        alignRight={true}
        footer={
          <Button onClick={handleSave}>{data.id ? 'Save Changes' : 'Create Quote Rate'}</Button>
        }
        onClose={onClose}
        show={show}
        title={data.id ? 'Editing Quote Rate' : 'New Quote Rate'}
        wide={true}
      >
        {save_error ? (
          <Alert type='error'>An error occurred while trying to save the quote rate.</Alert>
        ) : null}

        <Field label='Product'>
          <Select
            onChange={handleInputChange.bind(null, 'product_id')}
            options={products.map(p => ({ value: p.id, label: p.product_name }))}
            value={data.product_id}
          />
        </Field>

        <Field label='Band'>
          <Select
            onChange={handleInputChange.bind(null, 'band_id')}
            options={bands.map(b => ({ value: b.id, label: b.band_name }))}
            value={data.band_id}
          />
        </Field>

        <Field label='Modal Factor'>
          <Select
            onChange={handleInputChange.bind(null, 'modal_factor_id')}
            options={modalFactors.map(mf => ({ value: mf.id, label: mf.name }))}
            value={data.modal_factor_id}
          />
        </Field>

        <Field label='Health Category'>
          <Select
            onChange={handleInputChange.bind(null, 'health_category_id')}
            options={healthCategories.map(hc => ({
              value: hc.id,
              label: hc.carrier_health_category
            }))}
            value={data.health_category_id}
          />
        </Field>

        <Field label='Gender'>
          <Select
            onChange={handleInputChange.bind(null, 'gender')}
            options={[{ value: 'Both', label: 'Unisex' }, { value: 'Male' }, { value: 'Female' }]}
            value={data.gender}
          />
        </Field>

        <Field
          description='If you want these rates to apply to table rated quotes, pick a table rate below.'
          label='Table Rate'
        >
          <Select
            onChange={handleInputChange.bind(null, 'table_rate_id')}
            options={[{ value: '', label: 'None' }].concat(
              tableRates.map(tr => ({ value: tr.id, label: tr.name }))
            )}
            value={data.table_rate_id}
          />
        </Field>

        <Field>
          <Checkbox
            checked={data.col_type === 'Table Rate'}
            label='Only apply these rates to table rates'
            onChange={handleColumnTypeChange}
          />
        </Field>

        <Field label='Accidental Death Benefit Riders'>
          <Checklist
            onChange={handleChecklistChange.bind(null, 'adb_rider_ids')}
            options={riders
              .filter(r => r.rider_type === 'adb')
              .map(r => ({ value: r.id, label: r.name }))}
            selected={data.adb_rider_ids}
            showSelectAll={true}
          />
        </Field>

        <Field label='Child Riders'>
          <Checklist
            onChange={handleChecklistChange.bind(null, 'child_rider_ids')}
            options={riders
              .filter(r => r.rider_type === 'child')
              .map(r => ({ value: r.id, label: r.name }))}
            selected={data.child_rider_ids}
            showSelectAll={true}
          />
        </Field>

        <Field label='Child + Waiver of Premium Riders'>
          <Checklist
            onChange={handleChecklistChange.bind(null, 'child_wop_rider_ids')}
            options={riders
              .filter(r => r.rider_type === 'child_wop')
              .map(r => ({ value: r.id, label: r.name }))}
            selected={data.child_wop_rider_ids}
            showSelectAll={true}
          />
        </Field>

        <Field label='Waiver of Premium Riders'>
          <Checklist
            onChange={handleChecklistChange.bind(null, 'wop_rider_ids')}
            options={riders
              .filter(r => r.rider_type === 'wop')
              .map(r => ({ value: r.id, label: r.name }))}
            selected={data.wop_rider_ids}
            showSelectAll={true}
          />
        </Field>

        <Field label='States'>
          <Checklist
            columns={3}
            onChange={handleChecklistChange.bind(null, 'states')}
            options={STATES}
            selected={data.states}
            showSelectAll={true}
          />
        </Field>

        {data.id ? (
          <Field
            action={{
              onClick: setAgeRate.bind(null, { quote_rate_id: data.id }),
              label: 'Create New Age Rate Set'
            }}
            label='Age Rate Sets'
          >
            <Table
              columns={[
                { id: 'id', label: 'ID' },
                {
                  id: 'payment_modes',
                  label: 'Payment Modes',
                  render: age_rate => age_rate.payment_modes.map(pm => PAYMENT_MODES[pm]).join(', ')
                },
                {
                  id: 'actions',
                  render: rate => {
                    return rate.is_archived ? (
                      <Button
                        inline={true}
                        onClick={toggleAgeRateArchive.bind(null, rate)}
                        small={true}
                      >
                        Unarchive
                      </Button>
                    ) : (
                      <Button
                        color='RED'
                        inline={true}
                        onClick={toggleAgeRateArchive.bind(null, rate)}
                        small={true}
                      >
                        Archive
                      </Button>
                    );
                  }
                }
              ]}
              data={data.age_rates || []}
              onRowClick={rate => setAgeRate(rate)}
              placeholder='No age rates yet'
            />
          </Field>
        ) : null}
      </Drawer>

      {age_rate ? (
        <EditAgeRate ageRate={age_rate} onClose={setAgeRate.bind(null, null)} onSave={onSave} />
      ) : null}
    </>
  );
};

Edit.propTypes = {
  bands: PropTypes.array,
  healthCategories: PropTypes.array,
  modalFactors: PropTypes.array,
  onClose: PropTypes.func,
  onSave: PropTypes.func,
  products: PropTypes.array,
  quoteRate: PropTypes.object,
  riders: PropTypes.array,
  show: PropTypes.bool,
  tableRates: PropTypes.array
};

export default Edit;
